import React from "react";
import { ArrowUpRight, Info } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { useCta } from "@/context/SettingsContext";
import { Btn, Eyebrow, Section, SectionTitle } from "@/components/ui-kit/Kit";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";

const StepRail = ({ steps, dark }) => (
  <Stagger className="mt-14 grid gap-px border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-3">
    {steps.map((s, i) => (
      <StaggerItem key={s} className={dark ? "bg-navy" : "bg-white"}>
        <div className="group h-full p-8" data-testid={`step-${i + 1}`}>
          <span
            className={`font-display text-4xl font-bold leading-none ${
              dark ? "text-white/15" : "text-navy/12"
            } transition-colors duration-500 ${dark ? "group-hover:text-gold" : "group-hover:text-grn"}`}
          >
            {String(i + 1).padStart(2, "0")}
          </span>
          <p className={`mt-6 font-display text-lg font-medium ${dark ? "text-white" : "text-navy"}`}>{s}</p>
        </div>
      </StaggerItem>
    ))}
  </Stagger>
);

export const ProcessSteps = () => {
  const { t } = useI18n();
  const cta = useCta();
  const m = t("home.memberSteps");
  const v = t("home.vendorSteps");

  return (
    <>
      <Section className="py-24 sm:py-32" data-testid="member-steps-section">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <Reveal>
            <Eyebrow>{m.eyebrow}</Eyebrow>
            <SectionTitle className="mt-6 max-w-[20ch]">{m.title}</SectionTitle>
          </Reveal>
          <Reveal delay={0.1}>
            <Btn href={cta.member_registration_url || "#"} external variant="solid" data-testid="start-member-registration">
              {t("cta.startMemberRegistration")} <ArrowUpRight className="h-4 w-4" />
            </Btn>
          </Reveal>
        </div>
        <StepRail steps={m.steps} />
        <Reveal delay={0.1}>
          <p className="mt-8 flex max-w-3xl items-start gap-3 border-l-2 border-gold bg-slate-50 p-5 text-[12px] leading-relaxed text-slate-600">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
            {m.disclaimer}
          </p>
        </Reveal>
      </Section>

      <section
        className="relative overflow-hidden bg-navy py-24 text-white grain sm:py-32"
        data-testid="vendor-steps-section"
      >
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 hairline-grid" />
        <div className="relative container">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <Reveal>
              <Eyebrow tone="gold">{v.eyebrow}</Eyebrow>
              <SectionTitle dark className="mt-6 max-w-[20ch]">
                {v.title}
              </SectionTitle>
            </Reveal>
            <Reveal delay={0.1}>
              <Btn href={cta.vendor_registration_url || "#"} external variant="gold" data-testid="register-vendor-steps">
                {t("cta.registerVendor")} <ArrowUpRight className="h-4 w-4" />
              </Btn>
            </Reveal>
          </div>
          <Stagger className="mt-14 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {v.steps.map((s, i) => (
              <StaggerItem key={s} className="bg-navy">
                <div className="group h-full p-8" data-testid={`vendor-step-${i + 1}`}>
                  <span className="font-display text-4xl font-bold leading-none text-white/15 transition-colors duration-500 group-hover:text-gold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="mt-6 font-display text-lg font-medium">{s}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.1}>
            <p className="mt-8 flex max-w-3xl items-start gap-3 border-l-2 border-gold bg-white/5 p-5 text-[12px] leading-relaxed text-white/60">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              {v.disclaimer}
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
};
